import { NumericFormControllerText } from '@/components/util/TypedController.tsx';
import { betterHumanize } from '@/helpers/dayjs.ts';
import type {
  CustomShowProgramOption,
  FillerProgramOption,
} from '@/helpers/slotSchedulerUtil';
import { isSelectableForNewSlot } from '@/helpers/slotSchedulerUtil';
import { useAdjustRandomSlotWeights } from '@/hooks/slot_scheduler/useAdjustRandomSlotWeights.ts';
import { useRandomSlotFormContext } from '@/hooks/useRandomSlotFormContext.ts';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { TimeField } from '@mui/x-date-pickers';
import type { RandomSlot } from '@tunarr/types/api';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { isNil, map, values } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type { StrictOmit } from 'ts-essentials';
import { match } from 'ts-pattern';
import { v4 } from 'uuid';
import { deriveMidRollDefaults } from '../../helpers/midRollDefaults.ts';
import { useSlotProgramOptionsContext } from '../../hooks/programming_controls/useSlotProgramOptions.ts';
import { useFillerLists } from '../../hooks/useFillerLists.ts';
import useStore from '../../store/index.ts';
import type { LinkMode } from '../../model/CommonSlotModels.ts';
import {
  copySlotForLinking,
  slotIsLinkable,
} from '../../model/CommonSlotModels.ts';
import type { SlotViewModel } from '../../model/SlotModels.ts';
import { RouterLink } from '../base/RouterLink.tsx';
import { TabPanel } from '../TabPanel.tsx';
import { EditSlotProgrammingForm } from './EditSlotProgrammingForm.tsx';
import { MidRollConfigPanel } from './MidRollConfigPanel.tsx';
import { SlotFillerDialogPanel } from './SlotFillerDialogPanel.tsx';

type EditRandomSlotDialogContentProps = {
  slot: SlotViewModel;
  index: number;
  onSave: () => void;
  onCancel: () => void;
};

// Just the fields required for content
type PartialRandomSlot = StrictOmit<
  RandomSlot,
  'durationSpec' | 'cooldownMs' | 'weight'
> & { durationSpec?: RandomSlot['durationSpec'] };

export const EditRandomSlotDialogContent = ({
  slot,
  index,
  onSave,
  onCancel,
}: EditRandomSlotDialogContentProps) => {
  const { t } = useLingui();
  const randomSlotForm = useRandomSlotFormContext();
  const programOptions = useSlotProgramOptionsContext();
  const { slotArray } = randomSlotForm;
  const [currentSlots, distribution, lockWeights] = randomSlotForm.watch([
    'slots',
    'randomDistribution',
    'lockWeights',
  ]);

  const allSlots = useMemo(
    () =>
      currentSlots
        .filter((s): s is typeof s & { id: string } => 'id' in s)
        .map((s) => ({ ...s })),
    [currentSlots],
  );

  const linkableSlots = useMemo(
    () => allSlots.filter(slotIsLinkable),
    [allSlots],
  );

  const handleLinkSourceSlot = useCallback(
    (sourceSlotId: string, groupId: string, linkMode: LinkMode) => {
      const slots = randomSlotForm.getValues('slots');
      const idx = slots.findIndex(
        (s) => slotIsLinkable(s) && s.id === sourceSlotId,
      );
      if (idx !== -1) {
        const field = slots[idx];
        if (slotIsLinkable(field)) {
          slotArray.update(idx, {
            ...field,
            iterationGroup: groupId,
            linkMode,
          });
        }
      }
    },
    [slotArray, randomSlotForm],
  );

  const handleUnlinkFromGroup = useCallback(
    (groupId: string) => {
      const currentSlotId = slotIsLinkable(slot) ? slot.id : undefined;
      const slots = randomSlotForm.getValues('slots');
      const peersInGroup = slots.filter(
        (s) =>
          slotIsLinkable(s) &&
          s.iterationGroup === groupId &&
          s.id !== currentSlotId,
      );
      if (peersInGroup.length <= 1) {
        const newSlots = slots.map((s) => {
          if (
            slotIsLinkable(s) &&
            s.iterationGroup === groupId &&
            s.id !== currentSlotId
          ) {
            return {
              ...s,
              iterationGroup: undefined,
              linkMode: undefined,
              rerunOverflow: undefined,
            };
          }
          return s;
        });
        randomSlotForm.setValue('slots', newSlots, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [randomSlotForm, slot],
  );

  const formMethods = useForm<SlotViewModel>({
    defaultValues: slot,
    reValidateMode: 'onChange',
  });

  const { control, getValues, setValue, watch, formState } = formMethods;
  const { isValid, isDirty } = formState;
  const [durationSpec, programType, fillerValues] = watch([
    'durationSpec',
    'type',
    'filler',
  ]);
  const hasMidFiller =
    (fillerValues as { types?: string[] }[] | undefined)?.some((f) =>
      f.types?.includes('mid'),
    ) ?? false;
  const [tab, setTab] = useState(0);
  const { data: fillerLists } = useFillerLists();
  const programLookup = useStore((s) => s.programLookup);
  const channelPrograms = useMemo(() => values(programLookup), [programLookup]);

  useEffect(() => {
    if (!hasMidFiller && tab === 2) {
      setTab(0);
    }
    if (hasMidFiller && !getValues('midRoll')) {
      setValue('midRoll', deriveMidRollDefaults(getValues(), channelPrograms));
    }
  }, [hasMidFiller, tab, getValues, setValue, channelPrograms]);

  const [weightValue, setWeightValue] = useState(getValues('weight'));

  const handleWeightChange = (_: Event, newValue: number | number[]) => {
    setWeightValue(newValue as number);
  };

  const adjustSlotWeights = useAdjustRandomSlotWeights();

  const setFormWeightValue = (
    _: React.SyntheticEvent | Event,
    newValue: number,
  ) => {
    if (!lockWeights) {
      setValue('weight', newValue, { shouldDirty: true, shouldTouch: true });
      randomSlotForm.setValue(
        'slots',
        currentSlots.map((slot, slotIdx) =>
          slotIdx === index ? { ...slot, weight: newValue } : slot,
        ),
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
      return;
    }

    const newWeights = adjustSlotWeights(
      currentSlots.map((slot) => slot.weight),
      index,
      newValue,
      1,
    );

    if (newWeights) {
      setValue('weight', newWeights[index], {
        shouldDirty: true,
        shouldTouch: true,
      });

      randomSlotForm.setValue(
        'slots',
        map(currentSlots, (slot, idx) => ({
          ...slot,
          weight: newWeights[idx],
        })),
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
    }
  };

  const commit = () => {
    const saved = getValues();
    const linkable = slotIsLinkable(saved) ? saved : undefined;
    const groupId = linkable?.iterationGroup;
    const linkedContent =
      groupId && linkable ? copySlotForLinking(linkable) : undefined;

    if (linkedContent && groupId) {
      const currentSlots = randomSlotForm.getValues('slots');
      const newSlots = currentSlots.map((s, i) => {
        if (i === index) return saved;
        if (slotIsLinkable(s) && s.iterationGroup === groupId) {
          return { ...s, ...linkedContent };
        }
        return s;
      });
      randomSlotForm.setValue('slots', newSlots, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      slotArray.update(index, saved);
    }

    onSave();
  };

  const updateSlotTime = useCallback(
    (
      fieldValue: Dayjs | null,
      originalOnChange: (...args: unknown[]) => void,
    ) => {
      if (!fieldValue) return;
      const h = fieldValue.hour();
      const m = fieldValue.minute();
      const millis = dayjs.duration({ hours: h, minutes: m }).asMilliseconds();
      originalOnChange(millis);
    },
    [],
  );

  const newSlotForType = useCallback(
    (type: RandomSlot['type']) => {
      // Flex and redirect slots only take a fixed duration, and their editor
      // hides the Fixed/Dynamic toggle, so replace a dynamic spec here.
      const fixedDurationSpec = (): RandomSlot['durationSpec'] => {
        const current = getValues('durationSpec');
        return current.type === 'fixed'
          ? current
          : {
              type: 'fixed',
              durationMs: dayjs.duration({ minutes: 30 }).asMilliseconds(),
            };
      };

      return match(type)
        .returnType<PartialRandomSlot>()
        .with('custom-show', () => {
          const opt = programOptions
            .filter(isSelectableForNewSlot)
            .find(
              (opt): opt is CustomShowProgramOption =>
                opt.type === 'custom-show',
            );
          if (opt === undefined) {
            throw new Error(
              'Custom show slots are only offered when a custom show has programs',
            );
          }
          return {
            id: v4(),
            type: 'custom-show',
            order: 'next',
            direction: 'asc',
            customShowId: opt.customShowId,
          };
        })
        .with('filler', () => ({
          id: v4(),
          type: 'filler',
          order: 'shuffle_prefer_short',
          direction: 'asc',
          decayFactor: 0.5,
          durationWeighting: 'linear',
          recoveryFactor: 0.05,
          fillerListId: programOptions.find(
            (opt): opt is FillerProgramOption => opt.type === 'filler',
          )!.fillerListId,
        }))
        .with('flex', () => ({
          type: 'flex',
          order: 'next',
          direction: 'asc',
          durationSpec: fixedDurationSpec(),
        }))
        .with('redirect', () => ({
          type: 'redirect',
          channelId: programOptions.find((opt) => opt.type === 'redirect')!
            .channelId,
          order: 'next',
          direction: 'asc',
          durationSpec: fixedDurationSpec(),
        }))
        .with('show', () => ({
          id: v4(),
          type: 'show',
          showId: programOptions.find((opt) => opt.type === 'show')!.showId,
          order: 'next',
          direction: 'asc',
          seasonFilter: [],
          seasonExcludeFilter: [],
        }))
        .with('smart-collection', () => {
          const opt = programOptions.find(
            (opt) => opt.type === 'smart-collection',
          );
          return {
            id: v4(),
            type: 'smart-collection' as const,
            order: 'next',
            direction: 'asc',
            smartCollectionId: opt?.collectionId ?? '',
          };
        })
        .exhaustive();
    },
    [getValues, programOptions],
  );

  // const slotId = getRandomSlotId(programming);
  // const programDetails = useScheduledSlotProgramDetails([slotId]);
  // TODO: Hook this up to a waring icon on the duration input
  // to show that the slot is not large enough for the scheduled programs
  // const overTimeCount = useMemo(() => {
  //   if (programDetails[slotId]) {
  //     return countWhere(
  //       programDetails[slotId]?.programDurations,
  //       ({ duration }) => duration > slotDuration,
  //     );
  //   }
  //   return 0;
  // }, [programDetails, slotDuration, slotId]);

  const handleDurationTypeChange = useCallback(
    (value: RandomSlot['durationSpec']['type']) => {
      if (value === 'fixed') {
        setValue(
          'durationSpec',
          {
            durationMs: dayjs.duration({ minutes: 30 }).asMilliseconds(),
            type: 'fixed',
          },
          {
            shouldDirty: true,
          },
        );
      } else {
        setValue(
          'durationSpec',
          {
            type: 'dynamic',
            programCount: 1,
          },
          { shouldDirty: true },
        );
      }
    },
    [setValue],
  );

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            pt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, tab: number) => setTab(tab)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t`Programming`} value={0} />
            {programType !== 'flex' && <Tab label={t`Filler`} value={1} />}
            {hasMidFiller && <Tab label={t`Mid-Roll`} value={2} />}
          </Tabs>
          <TabPanel value={tab} index={0}>
            <Stack gap={2} useFlexGap>
              <Stack direction="row" gap={1}>
                {programType === 'flex' || programType === 'redirect' ? null : (
                  <Box>
                    <Controller
                      control={control}
                      name="durationSpec.type"
                      render={({ field }) => (
                        <ToggleButtonGroup
                          color="primary"
                          exclusive
                          aria-label="Platform"
                          {...field}
                          onChange={(_, value) =>
                            handleDurationTypeChange(
                              value as RandomSlot['durationSpec']['type'],
                            )
                          }
                        >
                          <ToggleButton value="fixed">
                            <Trans>Fixed</Trans>
                          </ToggleButton>
                          <ToggleButton value="dynamic">
                            <Trans>Dynamic</Trans>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />
                  </Box>
                )}
                {durationSpec.type === 'dynamic' && (
                  <NumericFormControllerText
                    control={control}
                    TextFieldProps={{
                      label: t`Program Count`,
                      fullWidth: true,
                      helperText: '',
                    }}
                    rules={{
                      min: durationSpec.type === 'dynamic' ? 1 : undefined,
                    }}
                    defaultValue={1}
                    name="durationSpec.programCount"
                  />
                )}
                {durationSpec.type === 'fixed' && (
                  <Controller
                    control={control}
                    name="durationSpec.durationMs"
                    rules={{
                      min: durationSpec.type === 'fixed' ? 1 : undefined,
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TimeField
                          format="H[h] m[m] s[s]"
                          {...field}
                          value={dayjs().startOf('day').add(field.value)}
                          onChange={(value) =>
                            updateSlotTime(value, field.onChange)
                          }
                          label={t`Duration`}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !isNil(error),
                              helperText: betterHumanize(
                                dayjs.duration(field.value),
                                { exact: true, style: 'full' },
                              ),
                            },
                          }}
                        />
                      );
                    }}
                  />
                )}
                {distribution !== 'none' && (
                  <Controller
                    control={control}
                    name="cooldownMs"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TimeField
                          format="H[h] m[m] s[s]"
                          {...field}
                          value={dayjs().startOf('day').add(field.value)}
                          onChange={(value) =>
                            updateSlotTime(value, field.onChange)
                          }
                          label={t`Cooldown`}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !isNil(error),
                              helperText: betterHumanize(
                                dayjs.duration(field.value),
                                { exact: true, style: 'full' },
                              ),
                            },
                          }}
                        />
                      );
                    }}
                  />
                )}
              </Stack>
              <FormProvider {...formMethods}>
                <EditSlotProgrammingForm
                  newSlotForType={newSlotForType}
                  allSlots={linkableSlots}
                  onLinkSourceSlot={handleLinkSourceSlot}
                  onUnlinkFromGroup={handleUnlinkFromGroup}
                />
              </FormProvider>
              {distribution === 'weighted' && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Slider
                    min={0}
                    max={100}
                    value={weightValue}
                    step={0.1}
                    onChange={handleWeightChange}
                    onChangeCommitted={setFormWeightValue}
                    sx={{
                      width: '90%',
                      '& .MuiSlider-thumb': {
                        transition: 'left 0.1s',
                      },
                      '& .MuiSlider-thumb.MuiSlider-active': {
                        transition: 'left 0s',
                      },
                      '& .MuiSlider-track': {
                        transition: 'width 0.1s',
                      },
                    }}
                  />
                  <TextField
                    type="number"
                    label={lockWeights ? t`Weight %` : t`Frequency`}
                    value={weightValue}
                    disabled
                  />
                </Stack>
              )}
            </Stack>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            {fillerLists.length === 0 ? (
              <Alert severity="warning" variant="outlined">
                <Trans>
                  You must create at least one{' '}
                  <RouterLink to="/library/fillers">filler list</RouterLink>{' '}
                  before assigning filler to a lot.
                </Trans>
              </Alert>
            ) : (
              <FormProvider {...formMethods}>
                <SlotFillerDialogPanel />
              </FormProvider>
            )}
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <FormProvider {...formMethods}>
              <MidRollConfigPanel />
            </FormProvider>
          </TabPanel>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCancel()}>
          <Trans>Cancel</Trans>
        </Button>
        <Button
          disabled={!isDirty || !isValid}
          onClick={() => commit()}
          variant="contained"
        >
          <Trans>Save</Trans>
        </Button>
      </DialogActions>
    </>
  );
};
